/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react'
import axios from 'axios';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import type { Book } from '../types/book.types';

const LOCAL_STORAGE_KEY = 'bookstore_local_cart';

interface CartItem {
  id: number;
  book_id: number;
  title: string;
  author: string;
  price: number;
  image?: string;
  category: string;
  stock: number;
  quantity: number;
}


interface CartContextType {
  cartItems: CartItem[];
  addToCart: (book: Book, quantity?: number) => Promise<void>;
  removeFromCart: (bookId: number) => Promise<void>;
  updateCartItemQuantity: (bookId: number, quantity: number) => Promise<void>;
  isInCart: (bookId: number) => boolean;
  canAddToCart: (bookId: number, quantity?: number) => { canAdd: boolean; reason?: string; currentQuantity: number };
  clearCart: (showToast?: boolean) => Promise<void>;
  loading: boolean;
  isAddingToCart: number | null;
  cartTotal: number;
  cartItemCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState<number | null>(null);

  // Calculate cart total
  const cartTotal = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  
  // Calculate total item count
  const cartItemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  // Load local cart from localStorage
  const loadLocalCart = () => {
    try {
      const localCart = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localCart) {
        const parsedCart = JSON.parse(localCart);
        return parsedCart;
      }
    } catch (error) {
      console.error('Failed to load local cart:', error);
    }
    return [];
  };

  // Save cart to localStorage
  const saveLocalCart = (items: CartItem[]) => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Failed to save local cart:', error);
    }
  };

  // Sync local cart to server and merge with server cart
  const syncLocalCartToServer = async (token: string) => {
    try {
      const localCart = loadLocalCart();
      
      if (localCart.length === 0) {
        // No local cart to sync
        return;
      }

      // Get current server cart
      const serverResponse = await axios.get(`${import.meta.env.VITE_API_URL}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const serverCart = serverResponse.data.data || [];

      // Add each item from local cart to server, merging quantities
      for (const localItem of localCart) {
        const existingServerItem = serverCart.find((item: CartItem) => item.book_id === localItem.book_id);
        
        try {
          if (existingServerItem) {
            // If item exists on server, add the local quantity to server quantity
            const newQuantity = existingServerItem.quantity + localItem.quantity;
            await axios.put(
              `${import.meta.env.VITE_API_URL}/cart/${localItem.book_id}`,
              { quantity: newQuantity },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            // If item doesn't exist on server, add it
            await axios.post(
              `${import.meta.env.VITE_API_URL}/cart`,
              { bookId: localItem.book_id, quantity: localItem.quantity },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
        } catch (error) {
          console.error('Failed to sync item:', error);
        }
      }

      // Clear local cart after successful sync
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      
      console.log('Local cart synced to server successfully');
    } catch (error) {
      console.error('Failed to sync local cart to server:', error);
    }
  };

  // Load cart from API on component mount or when auth status changes
  useEffect(() => {
    const loadCart = async () => {
      setLoading(true);
      try {
        if (isAuthenticated) {
          const token = localStorage.getItem('token');
          if (!token) {
            setCartItems([]);
            return;
          }

          // First, sync local cart to server
          await syncLocalCartToServer(token);
          
          // Then fetch the merged cart from server
          const response = await axios.get(`${import.meta.env.VITE_API_URL}/cart`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.status === 'success') {
            setCartItems(response.data.data);
          }
        } else {
          // Load local cart for unauthenticated users
          const localCart = loadLocalCart();
          setCartItems(localCart);
        }
      } catch (error) {
        console.error('Failed to load cart:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, [isAuthenticated]);

  const addToCart = async (book: Book, quantity: number = 1) => {
    // Get the book ID and ensure it's a number
    const bookId = parseInt(String(book.book_id || book.id));
    
    if (isNaN(bookId)) {
      toast.error('معرف الكتاب غير صالح');
      return;
    }
    
    setIsAddingToCart(bookId);

    try {
      if (isAuthenticated) {
        // Add to server cart for authenticated users
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('جلسة المستخدم منتهية، يرجى إعادة تسجيل الدخول');
          setIsAddingToCart(null);
          return;
        }
        
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/cart`,
          { bookId, quantity },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.status === 'success') {
          if (response.data.data && Array.isArray(response.data.data)) {
            setCartItems(response.data.data);
          }
          toast.success('تمت إضافة الكتاب إلى عربة التسوق');
        } else {
          toast.error(response.data.message || 'حدث خطأ أثناء إضافة الكتاب إلى عربة التسوق');
        }
      } else {
        // Add to local cart for unauthenticated users - NO LOGIN REQUIRED
        const existingItem = cartItems.find(item => item.book_id === bookId);
        
        let updatedCart: CartItem[];
        if (existingItem) {
          updatedCart = cartItems.map(item =>
            item.book_id === bookId
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          const newItem: CartItem = {
            id: cartItems.length + 1,
            book_id: bookId,
            title: book.title,
            author: book.author,
            price: book.price,
            image: book.image,
            category: book.category,
            stock: book.stock,
            quantity
          };
          updatedCart = [...cartItems, newItem];
        }
        
        setCartItems(updatedCart);
        saveLocalCart(updatedCart);
        toast.success('تمت إضافة الكتاب إلى عربة التسوق');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'حدث خطأ أثناء إضافة الكتاب إلى عربة التسوق';
      toast.error(errorMessage);
    } finally {
      setIsAddingToCart(null);
    }
  };

  const removeFromCart = async (bookId: number) => {
    try {
      if (isAuthenticated) {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${import.meta.env.VITE_API_URL}/cart/${bookId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status === 'success') {
          setCartItems(prev => prev.filter(item => item.book_id !== bookId));
          toast.success('تم إزالة الكتاب من عربة التسوق');
        }
      } else {
        // Remove from local cart
        const updatedCart = cartItems.filter(item => item.book_id !== bookId);
        setCartItems(updatedCart);
        saveLocalCart(updatedCart);
        toast.success('تم إزالة الكتاب من عربة التسوق');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('حدث خطأ أثناء إزالة الكتاب من عربة التسوق');
    }
  };

  const updateCartItemQuantity = async (bookId: number, quantity: number) => {
    if (quantity <= 0) {
      return removeFromCart(bookId);
    }

    try {
      if (isAuthenticated) {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          `${import.meta.env.VITE_API_URL}/cart/${bookId}`,
          { quantity },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.status === 'success') {
          setCartItems(prev => prev.map(item => {
            if (item.book_id === bookId) {
              return { ...item, quantity };
            }
            return item;
          }));
          toast.success('تم تحديث كمية الكتاب');
        }
      } else {
        // Update local cart
        const updatedCart = cartItems.map(item => {
          if (item.book_id === bookId) {
            return { ...item, quantity };
          }
          return item;
        });
        setCartItems(updatedCart);
        saveLocalCart(updatedCart);
        toast.success('تم تحديث كمية الكتاب');
      }
    } catch (error: any) {
      console.error('Error updating cart item quantity:', error);
      const errorMessage = error.response?.data?.message || 'حدث خطأ أثناء تحديث الكمية';
      toast.error(errorMessage);
    }
  };

  const clearCart = async (showToast: boolean = true) => {
    try {
      if (isAuthenticated) {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${import.meta.env.VITE_API_URL}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status === 'success') {
          setCartItems([]);
          if (showToast) toast.success('تم مسح عربة التسوق');
        }
      } else {
        // Clear local cart
        setCartItems([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        if (showToast) toast.success('تم مسح عربة التسوق');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      if (showToast) toast.error('حدث خطأ أثناء مسح عربة التسوق');
    }
  };

  const isInCart = (bookId: number) => {
    return cartItems.some(item => item.book_id === bookId);
  };

  const canAddToCart = (bookId: number, quantity: number = 1) => {
    const existingItem = cartItems.find(item => item.book_id === bookId);
    const currentQuantity = existingItem?.quantity || 0;
    const newTotalQuantity = currentQuantity + quantity;
    
    const availableStock = existingItem?.stock || 0;
    
    if (availableStock === 0) {
      return { canAdd: false, reason: 'الكتاب غير متوفر', currentQuantity };
    }
    
    if (newTotalQuantity > availableStock) {
      return { 
        canAdd: false, 
        reason: `الكمية المتوفرة: ${availableStock}. لديك ${currentQuantity} في السلة بالفعل`, 
        currentQuantity 
      };
    }
    
    return { canAdd: true, currentQuantity };
  };

  return (
    <CartContext.Provider value={{ 
      cartItems, 
      addToCart,
      removeFromCart,
      updateCartItemQuantity, 
      isInCart,
      canAddToCart,
      clearCart,
      loading,
      isAddingToCart,
      cartTotal,
      cartItemCount    
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};